import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import React, { FC, useCallback, useState, MouseEvent } from 'react';
import { CardAction } from 'shared/interfaces';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { ActionGuard } from '../ActionGuard';
import { useStyles } from './UserCardMenu.styles';

interface UserCardMenuProps<T = never> {
  data?: unknown;
  readonly?: boolean;
  actions?: CardAction<T>[];
}

export const UserCardMenu: FC<UserCardMenuProps> = ({ data, actions, readonly, children }) => {
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const onOpenHandler = useCallback((event: MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);
  const onCloseHandler = useCallback(() => setAnchorEl(null), []);

  const onSelectHandler = useCallback(
    (event, action) => {
      event.stopPropagation();
      action?.handler(data);
      onCloseHandler();
    },
    [onCloseHandler, data]
  );

  if (readonly) {
    return (
      <IconButton aria-label="menu" className={classes.button}>
        <MoreHorizIcon fontSize="medium" />
      </IconButton>
    );
  }

  if ((!data || !actions?.length) && !children) {
    return null;
  }

  return (
    <Box ml={children ? 0 : 2}>
      {children ? (
        <Box onClick={onOpenHandler}>{children}</Box>
      ) : (
        <IconButton aria-label="menu" className={classes.button} onClick={onOpenHandler}>
          <MoreHorizIcon fontSize="medium" />
        </IconButton>
      )}
      <Menu
        id="menu"
        open={open}
        anchorEl={anchorEl}
        onClose={onCloseHandler}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {actions?.map((action, index) => (
          <ActionGuard key={index} disabled={action?.isAllowedWithoutAuthorization}>
            <MenuItem
              onClick={(event) => onSelectHandler(event, action)}
              disabled={action.isDisabled}
            >
              {action?.label}
            </MenuItem>
          </ActionGuard>
        ))}
      </Menu>
    </Box>
  );
};
